import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import type { GridMap } from "../generation/GridMap";
import type { Point } from "../generation/types";
import { gridToWorld } from "../rendering/worldCoordinates";
import type { AudioSystem } from "./AudioSystem";

export interface ExitSystemOptions {
  readonly initiallyLocked?: boolean;
  readonly onBlocked?: () => void;
  readonly onComplete: () => void;
}

export class ExitSystem {
  public readonly mesh: Mesh;
  private activated = false;
  private locked: boolean;
  private readonly material: StandardMaterial;

  public constructor(
    exit: Point,
    grid: GridMap,
    scene: Scene,
    private readonly audio: AudioSystem,
    private readonly options: ExitSystemOptions,
  ) {
    this.locked = options.initiallyLocked ?? false;
    const world = gridToWorld(exit, grid);
    this.mesh = MeshBuilder.CreateBox(
      "exit-switch",
      { width: 1.1, height: 1.2, depth: 0.32 },
      scene,
    );
    this.mesh.position.set(world.x, 1.15, world.z);
    this.material = new StandardMaterial("exit-switch-material", scene);
    this.material.diffuseColor = new Color3(0.05, 0.12, 0.1);
    this.updateMaterial();
    this.mesh.material = this.material;
    this.mesh.isPickable = true;
    this.mesh.metadata = { exitSwitch: true, surface: "switch" };
  }

  public isUsable(position: Vector3, forward: Vector3, range: number): boolean {
    if (this.activated) return false;
    const offsetX = this.mesh.position.x - position.x;
    const offsetY = this.mesh.position.y - position.y;
    const offsetZ = this.mesh.position.z - position.z;
    const distance = Math.hypot(offsetX, offsetY, offsetZ);
    if (distance > range || distance <= 0.001) return false;
    return (offsetX * forward.x + offsetY * forward.y + offsetZ * forward.z) / distance > 0.3;
  }

  public activate(): void {
    if (this.activated) return;
    if (this.locked) {
      this.audio.play("switch");
      this.options.onBlocked?.();
      return;
    }
    this.activated = true;
    this.audio.play("switch");
    this.audio.play("level_complete");
    this.material.emissiveColor = new Color3(0.95, 0.2, 0.08);
    this.options.onComplete();
  }

  public unlock(): void {
    if (!this.locked) return;
    this.locked = false;
    this.updateMaterial();
  }

  public get prompt(): string {
    return this.locked ? "E — ВЫХОД ЗАПЕЧАТАН ТИРАНОМ" : "E — НАЧАТЬ ЭВАКУАЦИЮ";
  }

  public get isLocked(): boolean {
    return this.locked;
  }

  private updateMaterial(): void {
    this.material.emissiveColor = this.locked
      ? new Color3(0.72, 0.12, 0.04)
      : new Color3(0.06, 0.72, 0.55);
  }
}
